import React from 'react';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { Panel, HiddenChartTable, EmptyState } from './shared';
import { formatCount, formatPercent } from '../utils';
import { useInsightsStyles } from '../styles';
import { useColorMode } from '../../../../theme/ColorModeProvider';
import { getChartPalette } from '../../../../theme/colorMode';

const COLORS = ['#16a34a', '#3D7DDD', '#f59e0b', '#94a3b8', '#8b5cf6'];

/** `learners`: segments donut plus active / disabled accounts. */
const LearnerEngagement = ({ data = {} }) => {
  const classes = useInsightsStyles();
  const chart = getChartPalette(useColorMode().mode);
  const segments = data.segments || [];
  const active = Number(data.active_accounts) || 0;
  const disabled = Number(data.disabled_accounts) || 0;
  const accounts = active + disabled;
  return (
    <Panel>
      <Grid container spacing={3}>
        <Grid item xs={12} md={7}>
          <Typography component="h3" className={classes.sectionTitle}>Learner segments</Typography>
          {segments.length ? <>
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 16 }}>
              <div className={classes.chart} style={{ flex: '1 1 220px', maxWidth: 300, height: 240 }} role="img" aria-label="Donut chart of learner segments">
                <ResponsiveContainer>
                  <PieChart>
                    <Pie
                      data={segments}
                      dataKey="value"
                      nameKey="label"
                      innerRadius="62%"
                      outerRadius="92%"
                      minAngle={4}
                      paddingAngle={1}
                      stroke="var(--contentColor, #fff)"
                      strokeWidth={2}
                    >
                      {segments.map((item, index) => <Cell key={item.key} fill={COLORS[index % COLORS.length]} />)}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        background: chart.tooltipBg,
                        borderColor: chart.tooltipBorder,
                        borderRadius: 8,
                        color: chart.text,
                        boxShadow: '0 8px 20px rgba(0, 0, 0, .18)',
                      }}
                      itemStyle={{ color: chart.text }}
                      labelStyle={{ color: chart.text }}
                      formatter={(value, name, props) => [`${formatCount(value)} (${formatPercent(props.payload.pct)})`, name]}
                    />
                  </PieChart>
                </ResponsiveContainer>
                <div aria-hidden="true" style={{ position: 'absolute', top: 0, right: 0, bottom: 0, left: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
                  <span className={classes.muted} style={{ fontSize: 12 }}>Learners</span>
                  <span style={{ fontSize: 24, fontWeight: 700, lineHeight: 1.2 }}>{formatCount(data.total)}</span>
                </div>
              </div>
              <ul style={{ flex: '1 1 220px', minWidth: 0, listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 10 }}>
                {segments.map((item, index) => (
                  <li key={item.key} style={{ display: 'grid', gridTemplateColumns: '10px minmax(0, 1fr) auto', columnGap: 10, alignItems: 'baseline' }}>
                    <span
                      aria-hidden="true"
                      data-segment-color={COLORS[index % COLORS.length]}
                      style={{ width: 12, height: 12, borderRadius: 4, background: COLORS[index % COLORS.length], alignSelf: 'center' }}
                    />
                    <span style={{ minWidth: 0 }}>
                      <Typography style={{ fontSize: '.82rem', fontWeight: 700 }}>{item.label}</Typography>
                      {item.description ? <Typography className={classes.description} style={{ marginTop: 2 }}>{item.description}</Typography> : null}
                    </span>
                    <Typography style={{ fontSize: '.82rem', whiteSpace: 'nowrap' }}><strong>{formatCount(item.value)}</strong> <span className={classes.muted}>({formatPercent(item.pct)})</span></Typography>
                  </li>
                ))}
              </ul>
            </div>
            <HiddenChartTable caption="Learner segments" headers={['Segment', 'Learners', 'Percent', 'Description']} rows={segments.map(item => [item.label, item.value, formatPercent(item.pct), item.description])} />
          </> : <div style={{ marginTop: 12 }}><EmptyState /></div>}
        </Grid>
        <Grid item xs={12} md={5}>
          <Typography component="h3" className={classes.sectionTitle}>Accounts</Typography>
          <div style={{ display: 'flex', height: 18, borderRadius: 9, overflow: 'hidden', margin: '18px 0 12px', background: 'var(--dm-row-alt, #f8fafc)' }} aria-label={`${formatCount(active)} active and ${formatCount(disabled)} disabled accounts`}>
            <div style={{ width: `${accounts ? (active / accounts) * 100 : 0}%`, background: '#16a34a' }} /><div style={{ flex: 1, background: '#94a3b8' }} />
          </div>
          <Grid container spacing={2}>
            <Grid item xs={6}><div className={classes.tile}><Typography className={classes.sectionSubtitle} style={{ marginTop: 0 }}>Active accounts</Typography><Typography className={classes.metricValue}>{formatCount(active)}</Typography></div></Grid>
            <Grid item xs={6}><div className={classes.tile}><Typography className={classes.sectionSubtitle} style={{ marginTop: 0 }}>Disabled accounts</Typography><Typography className={classes.metricValue}>{formatCount(disabled)}</Typography></div></Grid>
          </Grid>
        </Grid>
      </Grid>
    </Panel>
  );
};

export default LearnerEngagement;
